import React from 'react'
import { Card, Row, Col, Spinner, ProgressBar } from 'react-bootstrap'
import Chart from 'react-apexcharts'
import { FinancialMetrics } from '../types'
import * as S from '../styles'

interface FinancialMetricsCardProps {
  data?: FinancialMetrics
  loading: boolean
}

// total = 0 (empresa sem dados) dava NaN% / R$ NaN na tela
const percentOf = (part: number, total: number) =>
  total > 0 ? (part / total) * 100 : 0

export const FinancialMetricsCard: React.FC<FinancialMetricsCardProps> = ({
  data,
  loading
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  if (loading || !data) {
    return (
      <Card className="h-100">
        <Card.Header>
          <Card.Title as="h5">
            <i className="feather icon-dollar-sign text-success mr-2" />
            Métricas Financeiras
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <S.LoadingSpinner>
            <Spinner animation="border" />
          </S.LoadingSpinner>
        </Card.Body>
      </Card>
    )
  }

  // Determine the appropriate scale based on data
  const maxValue = Math.max(...data.monthlyRevenue.map((item) => item.value))
  const minValue = Math.min(...data.monthlyRevenue.map((item) => item.value))
  const useMillions = maxValue >= 1000000

  // Chart for monthly revenue trend
  const chartOptions = {
    chart: {
      type: 'area' as const,
      height: 180,
      toolbar: {
        show: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth' as const,
      width: 3
    },
    xaxis: {
      categories: data.monthlyRevenue.map((item) => {
        const date = new Date(item.month + '-01')
        return date.toLocaleDateString('pt-BR', { month: 'short' })
      })
    },
    yaxis: {
      labels: {
        formatter: function (val: number) {
          if (useMillions) {
            return 'R$ ' + (val / 1000000).toFixed(1) + 'M'
          } else if (val >= 1000) {
            return 'R$ ' + (val / 1000).toFixed(0) + 'K'
          } else {
            return 'R$ ' + val.toFixed(0)
          }
        }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.3,
        stops: [0, 90, 100]
      }
    },
    colors: ['#28a745'],
    grid: {
      show: false
    }
  }

  const chartSeries = [
    {
      name: 'Receita',
      data: data.monthlyRevenue.map((item) => item.value)
    }
  ]

  const blockchainReceivablesPercentage = percentOf(
    data.totalReceivablesOnChain,
    data.totalBillingValue
  )

  return (
    <Card className="h-100">
      <Card.Header>
        <Card.Title as="h5">
          <i className="feather icon-dollar-sign text-success mr-2" />
          Métricas Financeiras
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Row className="mb-4">
          <Col sm={6}>
            <div className="text-center">
              <h3 className="text-success mb-1">
                {formatCurrency(data.totalBillingValue)}
              </h3>
              <p className="text-muted mb-0">Faturamento Total</p>
            </div>
          </Col>
          <Col sm={6}>
            <div className="text-center">
              <h3 className="text-info mb-1">
                {data.paymentCompletionRate.toFixed(1)}%
              </h3>
              <p className="text-muted mb-0">Taxa de Conclusão</p>
            </div>
          </Col>
        </Row>

        <div className="mb-4">
          <h6 className="mb-3">
            Tendência de Receita (±3 meses):
            {useMillions && (
              <small className="text-muted ml-2">(escala em milhões)</small>
            )}
          </h6>
          <S.ChartContainer>
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="area"
              height={180}
            />
          </S.ChartContainer>
        </div>

        <div className="mb-3">
          <h6 className="mb-3">Status dos Pagamentos:</h6>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-success">
                <i className="feather icon-check-circle mr-1" />
                Pagos
              </span>
              <span className="font-weight-bold">
                {formatNumber(data.paidBillings)}
              </span>
            </div>
            <small className="text-muted">
              {formatCurrency(
                (data.totalBillingValue *
                  percentOf(data.paidBillings, data.totalBillings)) /
                  100
              )}
            </small>
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-warning">
                <i className="feather icon-clock mr-1" />
                Pendentes
              </span>
              <span className="font-weight-bold">
                {formatNumber(data.pendingBillings)}
              </span>
            </div>
            <small className="text-muted">
              {formatCurrency(
                (data.totalBillingValue *
                  percentOf(data.pendingBillings, data.totalBillings)) /
                  100
              )}
            </small>
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-danger">
                <i className="feather icon-alert-circle mr-1" />
                Vencidos
              </span>
              <span className="font-weight-bold">
                {formatNumber(data.overdueBillings)}
              </span>
            </div>
            <small className="text-muted">
              {formatCurrency(
                (data.totalBillingValue *
                  percentOf(data.overdueBillings, data.totalBillings)) /
                  100
              )}
            </small>
          </div>
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">Recebíveis na Blockchain</span>
            <span className="font-weight-bold">
              {formatCurrency(data.totalReceivablesOnChain)}
            </span>
          </div>
          <ProgressBar
            now={blockchainReceivablesPercentage}
            variant="primary"
            style={{ height: '6px' }}
          />
          <small className="text-muted">
            {blockchainReceivablesPercentage.toFixed(1)}% registrados na
            blockchain
          </small>
        </div>

        <div className="mt-4 pt-3 border-top">
          <small className="text-muted">
            <i className="feather icon-link mr-1" />
            Todos os recebíveis são registrados de forma transparente na
            blockchain
          </small>
        </div>
      </Card.Body>
    </Card>
  )
}
